/**
 * Surface Modeler - NURBS and Freeform Surface Modeling for CAD
 * Handles parametric surface creation, editing, and analysis
 * Based on specifications in reports/draw/07-surface-modeling.md
 */

class SurfaceModeler {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.viewer = cadViewer.viewer;
    this.engine = cadViewer.engine;
    this.scene = cadViewer.scene;

    // Surface state
    this.surfaces = new Map(); // Map<surfaceId, SurfaceData>
    this.selectedSurfaceId = null;
    this.selectedCurves = []; // Curves selected for surface operations

    // Surface quality settings
    this.tessellationQuality = 0.1; // Lower = finer mesh
    this.divisions = { u: 32, v: 32 }; // Default divisions for parametric surfaces

    console.log('[SurfaceModeler] Initialized');
  }

  // ===== Surface Creation Operations =====

  /**
   * Extrude surface from curve
   * Creates a surface by extruding a curve along a direction
   */
  async extrudeSurface(curveId, distance, direction = { x: 0, y: 0, z: 1 }) {
    try {
      console.log('[SurfaceModeler] Extruding surface from curve:', curveId);

      const curve = this.getCurve(curveId);
      if (!curve) {
        throw new Error('Curve not found');
      }

      // Create extrusion geometry
      const geometry = this.createExtrusionGeometry(curve, distance, direction);

      // Create material
      const material = new THREE.MeshPhongMaterial({
        color: 0x4A90E2,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
        shininess: 30
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      // Add to scene
      this.scene.add(mesh);

      // Store surface data
      const surfaceId = Utils.generateId();
      const surfaceData = {
        id: surfaceId,
        type: 'extrude',
        name: `ExtrudedSurface_${surfaceId.substr(0, 6)}`,
        curveId: curveId,
        distance: distance,
        direction: direction,
        mesh: mesh,
        geometry: geometry,
        createdAt: new Date().toISOString()
      };

      this.surfaces.set(surfaceId, surfaceData);

      Utils.notify('Surface extruded successfully', 'success');
      this.viewer.engine.render();

      return surfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Extrude surface error:', error);
      Utils.notify('Failed to extrude surface: ' + error.message, 'error');
      throw error;
    }
  }

  /**
   * Revolve surface from curve
   * Creates a surface by revolving a curve around an axis
   */
  async revolveSurface(curveId, axis = { x: 0, y: 1, z: 0 }, angle = 360) {
    try {
      console.log('[SurfaceModeler] Revolving surface from curve:', curveId);

      const curve = this.getCurve(curveId);
      if (!curve) {
        throw new Error('Curve not found');
      }

      // Create revolution geometry
      const geometry = this.createRevolutionGeometry(curve, axis, angle);

      const material = new THREE.MeshPhongMaterial({
        color: 0x4A90E2,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
        shininess: 30
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      this.scene.add(mesh);

      const surfaceId = Utils.generateId();
      const surfaceData = {
        id: surfaceId,
        type: 'revolve',
        name: `RevolvedSurface_${surfaceId.substr(0, 6)}`,
        curveId: curveId,
        axis: axis,
        angle: angle,
        mesh: mesh,
        geometry: geometry,
        createdAt: new Date().toISOString()
      };

      this.surfaces.set(surfaceId, surfaceData);

      Utils.notify('Surface revolved successfully', 'success');
      this.viewer.engine.render();

      return surfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Revolve surface error:', error);
      Utils.notify('Failed to revolve surface: ' + error.message, 'error');
      throw error;
    }
  }

  /**
   * Loft surface between multiple curves
   * Creates a smooth surface connecting multiple profile curves
   */
  async loftSurface(curveIds, continuity = 'G1') {
    try {
      console.log('[SurfaceModeler] Lofting surface through curves:', curveIds);

      if (curveIds.length < 2) {
        throw new Error('At least 2 curves required for lofting');
      }

      const curves = curveIds.map(id => this.getCurve(id)).filter(c => c);
      if (curves.length !== curveIds.length) {
        throw new Error('One or more curves not found');
      }

      // Create loft geometry
      const geometry = this.createLoftGeometry(curves, continuity);

      const material = new THREE.MeshPhongMaterial({
        color: 0x4A90E2,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
        shininess: 30
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      this.scene.add(mesh);

      const surfaceId = Utils.generateId();
      const surfaceData = {
        id: surfaceId,
        type: 'loft',
        name: `LoftedSurface_${surfaceId.substr(0, 6)}`,
        curveIds: curveIds,
        continuity: continuity,
        mesh: mesh,
        geometry: geometry,
        createdAt: new Date().toISOString()
      };

      this.surfaces.set(surfaceId, surfaceData);

      Utils.notify('Surface lofted successfully', 'success');
      this.viewer.engine.render();

      return surfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Loft surface error:', error);
      Utils.notify('Failed to loft surface: ' + error.message, 'error');
      throw error;
    }
  }

  /**
   * Sweep surface along path
   * Creates a surface by sweeping a profile curve along a path curve
   */
  async sweepSurface(profileCurveId, pathCurveId) {
    try {
      console.log('[SurfaceModeler] Sweeping surface:', { profileCurveId, pathCurveId });

      const profile = this.getCurve(profileCurveId);
      const path = this.getCurve(pathCurveId);

      if (!profile || !path) {
        throw new Error('Profile or path curve not found');
      }

      // Create sweep geometry
      const geometry = this.createSweepGeometry(profile, path);

      const material = new THREE.MeshPhongMaterial({
        color: 0x4A90E2,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
        shininess: 30
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      this.scene.add(mesh);

      const surfaceId = Utils.generateId();
      const surfaceData = {
        id: surfaceId,
        type: 'sweep',
        name: `SweptSurface_${surfaceId.substr(0, 6)}`,
        profileCurveId: profileCurveId,
        pathCurveId: pathCurveId,
        mesh: mesh,
        geometry: geometry,
        createdAt: new Date().toISOString()
      };

      this.surfaces.set(surfaceId, surfaceData);

      Utils.notify('Surface swept successfully', 'success');
      this.viewer.engine.render();

      return surfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Sweep surface error:', error);
      Utils.notify('Failed to sweep surface: ' + error.message, 'error');
      throw error;
    }
  }

  /**
   * Offset surface
   * Creates a parallel surface at a specified distance
   */
  async offsetSurface(surfaceId, distance) {
    try {
      console.log('[SurfaceModeler] Offsetting surface:', surfaceId, 'by', distance);

      const surfaceData = this.surfaces.get(surfaceId);
      if (!surfaceData) {
        throw new Error('Surface not found');
      }

      // Create offset geometry by displacing vertices along normals
      const geometry = this.createOffsetGeometry(surfaceData.geometry, distance);

      const material = new THREE.MeshPhongMaterial({
        color: 0x4A90E2,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
        shininess: 30
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      this.scene.add(mesh);

      const newSurfaceId = Utils.generateId();
      const newSurfaceData = {
        id: newSurfaceId,
        type: 'offset',
        name: `OffsetSurface_${newSurfaceId.substr(0, 6)}`,
        baseSurfaceId: surfaceId,
        distance: distance,
        mesh: mesh,
        geometry: geometry,
        createdAt: new Date().toISOString()
      };

      this.surfaces.set(newSurfaceId, newSurfaceData);

      Utils.notify('Surface offset successfully', 'success');
      this.viewer.engine.render();

      return newSurfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Offset surface error:', error);
      Utils.notify('Failed to offset surface: ' + error.message, 'error');
      throw error;
    }
  }

  /**
   * Ruled surface between two curves
   * Creates a linear interpolation surface between two curves
   */
  async ruledSurface(curve1Id, curve2Id) {
    try {
      console.log('[SurfaceModeler] Creating ruled surface between:', curve1Id, curve2Id);

      const curve1 = this.getCurve(curve1Id);
      const curve2 = this.getCurve(curve2Id);

      if (!curve1 || !curve2) {
        throw new Error('One or both curves not found');
      }

      // Create ruled surface geometry
      const geometry = this.createRuledGeometry(curve1, curve2);

      const material = new THREE.MeshPhongMaterial({
        color: 0x4A90E2,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
        shininess: 30
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      this.scene.add(mesh);

      const surfaceId = Utils.generateId();
      const surfaceData = {
        id: surfaceId,
        type: 'ruled',
        name: `RuledSurface_${surfaceId.substr(0, 6)}`,
        curve1Id: curve1Id,
        curve2Id: curve2Id,
        mesh: mesh,
        geometry: geometry,
        createdAt: new Date().toISOString()
      };

      this.surfaces.set(surfaceId, surfaceData);

      Utils.notify('Ruled surface created successfully', 'success');
      this.viewer.engine.render();

      return surfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Ruled surface error:', error);
      Utils.notify('Failed to create ruled surface: ' + error.message, 'error');
      throw error;
    }
  }

  /**
   * Fill closed curve with surface
   * Creates a surface that fills a closed planar curve
   */
  async fillSurface(curveId) {
    try {
      console.log('[SurfaceModeler] Filling surface from closed curve:', curveId);

      const curve = this.getCurve(curveId);
      if (!curve) {
        throw new Error('Curve not found');
      }

      // Create fill geometry
      const geometry = this.createFillGeometry(curve);

      const material = new THREE.MeshPhongMaterial({
        color: 0x4A90E2,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
        shininess: 30
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      this.scene.add(mesh);

      const surfaceId = Utils.generateId();
      const surfaceData = {
        id: surfaceId,
        type: 'fill',
        name: `FilledSurface_${surfaceId.substr(0, 6)}`,
        curveId: curveId,
        mesh: mesh,
        geometry: geometry,
        createdAt: new Date().toISOString()
      };

      this.surfaces.set(surfaceId, surfaceData);

      Utils.notify('Surface filled successfully', 'success');
      this.viewer.engine.render();

      return surfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Fill surface error:', error);
      Utils.notify('Failed to fill surface: ' + error.message, 'error');
      throw error;
    }
  }

  /**
   * Trim surface with curves
   * Trims a surface using trimming curves
   */
  async trimSurface(surfaceId, trimmingCurveIds, keepInside = true) {
    try {
      console.log('[SurfaceModeler] Trimming surface:', surfaceId);

      const surfaceData = this.surfaces.get(surfaceId);
      if (!surfaceData) {
        throw new Error('Surface not found');
      }

      const trimmingCurves = trimmingCurveIds.map(id => this.getCurve(id)).filter(c => c);
      if (trimmingCurves.length !== trimmingCurveIds.length) {
        throw new Error('One or more trimming curves not found');
      }

      // Create trimmed geometry
      const geometry = this.createTrimmedGeometry(surfaceData.geometry, trimmingCurves, keepInside);

      // Update the existing mesh
      surfaceData.mesh.geometry.dispose();
      surfaceData.mesh.geometry = geometry;
      surfaceData.geometry = geometry;
      surfaceData.trimmed = true;
      surfaceData.trimmingCurveIds = trimmingCurveIds;
      surfaceData.keepInside = keepInside;

      Utils.notify('Surface trimmed successfully', 'success');
      this.viewer.engine.render();

      return surfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Trim surface error:', error);
      Utils.notify('Failed to trim surface: ' + error.message, 'error');
      throw error;
    }
  }

  /**
   * Extend surface
   * Extends a surface along its edges
   */
  async extendSurface(surfaceId, edge, distance, type = 'natural') {
    try {
      console.log('[SurfaceModeler] Extending surface:', surfaceId);

      const surfaceData = this.surfaces.get(surfaceId);
      if (!surfaceData) {
        throw new Error('Surface not found');
      }

      // Create extended geometry
      const geometry = this.createExtendedGeometry(surfaceData.geometry, edge, distance, type);

      // Update the existing mesh
      surfaceData.mesh.geometry.dispose();
      surfaceData.mesh.geometry = geometry;
      surfaceData.geometry = geometry;
      surfaceData.extended = true;
      surfaceData.extensionEdge = edge;
      surfaceData.extensionDistance = distance;
      surfaceData.extensionType = type;

      Utils.notify('Surface extended successfully', 'success');
      this.viewer.engine.render();

      return surfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Extend surface error:', error);
      Utils.notify('Failed to extend surface: ' + error.message, 'error');
      throw error;
    }
  }

  /**
   * Blend surface between two surfaces
   * Creates a G1/G2 continuous blend between two surfaces
   */
  async blendSurface(surface1Id, surface2Id, continuity = 'G1') {
    try {
      console.log('[SurfaceModeler] Creating blend surface between:', surface1Id, surface2Id);

      const surface1 = this.surfaces.get(surface1Id);
      const surface2 = this.surfaces.get(surface2Id);

      if (!surface1 || !surface2) {
        throw new Error('One or both surfaces not found');
      }

      // Create blend geometry
      const geometry = this.createBlendGeometry(surface1.geometry, surface2.geometry, continuity);

      const material = new THREE.MeshPhongMaterial({
        color: 0x4A90E2,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8,
        shininess: 30
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      this.scene.add(mesh);

      const surfaceId = Utils.generateId();
      const surfaceData = {
        id: surfaceId,
        type: 'blend',
        name: `BlendSurface_${surfaceId.substr(0, 6)}`,
        surface1Id: surface1Id,
        surface2Id: surface2Id,
        continuity: continuity,
        mesh: mesh,
        geometry: geometry,
        createdAt: new Date().toISOString()
      };

      this.surfaces.set(surfaceId, surfaceData);

      Utils.notify('Blend surface created successfully', 'success');
      this.viewer.engine.render();

      return surfaceId;

    } catch (error) {
      console.error('[SurfaceModeler] Blend surface error:', error);
      Utils.notify('Failed to create blend surface: ' + error.message, 'error');
      throw error;
    }
  }

  // ===== Geometry Creation Helpers =====

  /**
   * Create extrusion geometry from curve
   */
  createExtrusionGeometry(curve, distance, direction) {
    // Get points from curve
    const points = this.getCurvePoints(curve);

    // Normalize direction
    const dir = new THREE.Vector3(direction.x, direction.y, direction.z).normalize();

    // Create shape from points
    const shape = new THREE.Shape();
    shape.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      shape.lineTo(points[i].x, points[i].y);
    }

    // Extrude settings
    const extrudeSettings = {
      steps: 1,
      depth: distance,
      bevelEnabled: false
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    // Transform based on direction
    if (Math.abs(dir.x) > 0.1 || Math.abs(dir.y) > 0.1) {
      const quaternion = new THREE.Quaternion();
      quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);
      geometry.applyQuaternion(quaternion);
    }

    return geometry;
  }

  /**
   * Create revolution geometry from curve
   */
  createRevolutionGeometry(curve, axis, angle) {
    const points = this.getCurvePoints(curve);

    // Create 2D points for lathe
    const lathePoints = [];
    for (let i = 0; i < points.length; i++) {
      // Project point onto revolution plane
      const r = Math.sqrt(points[i].x * points[i].x + points[i].z * points[i].z);
      lathePoints.push(new THREE.Vector2(r, points[i].y));
    }

    // Convert angle to radians
    const angleRad = (angle * Math.PI) / 180;

    // Create lathe geometry
    const geometry = new THREE.LatheGeometry(lathePoints, this.divisions.u, 0, angleRad);

    return geometry;
  }

  /**
   * Create loft geometry from multiple curves
   */
  createLoftGeometry(curves, continuity) {
    // Get points from all curves
    const curvesPoints = curves.map(curve => this.getCurvePoints(curve));

    // Ensure all curves have the same number of points
    const pointCount = Math.max(...curvesPoints.map(cp => cp.length));
    const normalizedCurves = curvesPoints.map(cp => this.resampleCurve(cp, pointCount));

    // Create loft surface
    const vertices = [];
    const indices = [];

    // Build vertex grid
    for (let i = 0; i < normalizedCurves.length; i++) {
      for (let j = 0; j < normalizedCurves[i].length; j++) {
        const pt = normalizedCurves[i][j];
        vertices.push(pt.x, pt.y, pt.z);
      }
    }

    // Build faces
    for (let i = 0; i < normalizedCurves.length - 1; i++) {
      for (let j = 0; j < pointCount - 1; j++) {
        const a = i * pointCount + j;
        const b = i * pointCount + j + 1;
        const c = (i + 1) * pointCount + j + 1;
        const d = (i + 1) * pointCount + j;

        indices.push(a, b, c);
        indices.push(a, c, d);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  /**
   * Create sweep geometry
   */
  createSweepGeometry(profile, path) {
    const profilePoints = this.getCurvePoints(profile);
    const pathPoints = this.getCurvePoints(path);

    const vertices = [];
    const indices = [];

    // Create profile at each path point
    for (let i = 0; i < pathPoints.length; i++) {
      const pathPoint = pathPoints[i];

      // Get tangent at this point
      let tangent;
      if (i < pathPoints.length - 1) {
        tangent = new THREE.Vector3()
          .subVectors(pathPoints[i + 1], pathPoints[i])
          .normalize();
      } else {
        tangent = new THREE.Vector3()
          .subVectors(pathPoints[i], pathPoints[i - 1])
          .normalize();
      }

      // Create local coordinate system
      const up = new THREE.Vector3(0, 1, 0);
      const binormal = new THREE.Vector3().crossVectors(tangent, up).normalize();
      const normal = new THREE.Vector3().crossVectors(binormal, tangent).normalize();

      // Transform profile to path point
      for (let j = 0; j < profilePoints.length; j++) {
        const pp = profilePoints[j];
        const transformed = new THREE.Vector3(
          pathPoint.x + pp.x * normal.x + pp.y * binormal.x,
          pathPoint.y + pp.x * normal.y + pp.y * binormal.y,
          pathPoint.z + pp.x * normal.z + pp.y * binormal.z
        );
        vertices.push(transformed.x, transformed.y, transformed.z);
      }
    }

    // Build faces
    for (let i = 0; i < pathPoints.length - 1; i++) {
      for (let j = 0; j < profilePoints.length - 1; j++) {
        const a = i * profilePoints.length + j;
        const b = i * profilePoints.length + j + 1;
        const c = (i + 1) * profilePoints.length + j + 1;
        const d = (i + 1) * profilePoints.length + j;

        indices.push(a, b, c);
        indices.push(a, c, d);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  /**
   * Create offset geometry
   */
  createOffsetGeometry(originalGeometry, distance) {
    const geometry = originalGeometry.clone();

    // Get positions and normals
    const positions = geometry.attributes.position;
    const normals = geometry.attributes.normal || geometry.computeVertexNormals();

    // Offset each vertex along its normal
    for (let i = 0; i < positions.count; i++) {
      const normal = new THREE.Vector3(
        normals.array[i * 3],
        normals.array[i * 3 + 1],
        normals.array[i * 3 + 2]
      );

      positions.array[i * 3] += normal.x * distance;
      positions.array[i * 3 + 1] += normal.y * distance;
      positions.array[i * 3 + 2] += normal.z * distance;
    }

    positions.needsUpdate = true;
    geometry.computeVertexNormals();

    return geometry;
  }

  /**
   * Create ruled surface geometry
   */
  createRuledGeometry(curve1, curve2) {
    const points1 = this.getCurvePoints(curve1);
    const points2 = this.getCurvePoints(curve2);

    // Resample to same number of points
    const pointCount = Math.max(points1.length, points2.length);
    const resampledCurve1 = this.resampleCurve(points1, pointCount);
    const resampledCurve2 = this.resampleCurve(points2, pointCount);

    const vertices = [];
    const indices = [];

    // Add curve points
    for (let i = 0; i < pointCount; i++) {
      vertices.push(resampledCurve1[i].x, resampledCurve1[i].y, resampledCurve1[i].z);
    }
    for (let i = 0; i < pointCount; i++) {
      vertices.push(resampledCurve2[i].x, resampledCurve2[i].y, resampledCurve2[i].z);
    }

    // Build faces
    for (let i = 0; i < pointCount - 1; i++) {
      const a = i;
      const b = i + 1;
      const c = pointCount + i + 1;
      const d = pointCount + i;

      indices.push(a, b, c);
      indices.push(a, c, d);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  /**
   * Create fill geometry for closed curve
   */
  createFillGeometry(curve) {
    const points = this.getCurvePoints(curve);

    // Create shape
    const shape = new THREE.Shape();
    if (points.length > 0) {
      shape.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        shape.lineTo(points[i].x, points[i].y);
      }
      shape.closePath();
    }

    // Create geometry from shape
    const geometry = new THREE.ShapeGeometry(shape);

    return geometry;
  }

  /**
   * Create trimmed geometry
   */
  createTrimmedGeometry(geometry, trimmingCurves, keepInside) {
    // For now, return a copy of the original geometry
    // Full trimming implementation would require CSG operations
    console.warn('[SurfaceModeler] Surface trimming not fully implemented - returning original geometry');
    return geometry.clone();
  }

  /**
   * Create extended geometry
   */
  createExtendedGeometry(geometry, edge, distance, type) {
    // For now, return a copy of the original geometry
    // Full extension would require parametric surface manipulation
    console.warn('[SurfaceModeler] Surface extension not fully implemented - returning original geometry');
    return geometry.clone();
  }

  /**
   * Create blend geometry
   */
  createBlendGeometry(geometry1, geometry2, continuity) {
    // Simple blend: create a transition surface
    // Full implementation would require G2 continuity calculations
    const vertices = [];
    const indices = [];

    // Get edge vertices from both surfaces
    // This is a simplified version
    const pos1 = geometry1.attributes.position;
    const pos2 = geometry2.attributes.position;

    // Sample points from edges
    const edgePoints1 = [];
    const edgePoints2 = [];

    // For simplification, take some vertices from each geometry
    const sampleCount = 10;
    for (let i = 0; i < sampleCount; i++) {
      const idx1 = Math.floor((i / sampleCount) * pos1.count);
      const idx2 = Math.floor((i / sampleCount) * pos2.count);

      edgePoints1.push(new THREE.Vector3(
        pos1.array[idx1 * 3],
        pos1.array[idx1 * 3 + 1],
        pos1.array[idx1 * 3 + 2]
      ));

      edgePoints2.push(new THREE.Vector3(
        pos2.array[idx2 * 3],
        pos2.array[idx2 * 3 + 1],
        pos2.array[idx2 * 3 + 2]
      ));
    }

    // Create blend surface between edge points
    const blendSteps = 5;
    for (let i = 0; i <= blendSteps; i++) {
      const t = i / blendSteps;
      const smoothT = continuity === 'G2' ? this.smoothStep(t) : t;

      for (let j = 0; j < edgePoints1.length; j++) {
        const p = new THREE.Vector3().lerpVectors(edgePoints1[j], edgePoints2[j], smoothT);
        vertices.push(p.x, p.y, p.z);
      }
    }

    // Build faces
    for (let i = 0; i < blendSteps; i++) {
      for (let j = 0; j < edgePoints1.length - 1; j++) {
        const a = i * edgePoints1.length + j;
        const b = i * edgePoints1.length + j + 1;
        const c = (i + 1) * edgePoints1.length + j + 1;
        const d = (i + 1) * edgePoints1.length + j;

        indices.push(a, b, c);
        indices.push(a, c, d);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  // ===== Utility Functions =====

  /**
   * Get curve from CAD viewer
   */
  getCurve(curveId) {
    // Try to get curve from sketch engine
    if (this.cadViewer.sketchEngine && this.cadViewer.sketchEngine.entities) {
      const entity = this.cadViewer.sketchEngine.entities.find(e => e.id === curveId);
      if (entity) return entity;
    }

    // Try to get from document features
    if (this.cadViewer.document && this.cadViewer.document.features) {
      const feature = this.cadViewer.document.features.find(f => f.id === curveId);
      if (feature) return feature;
    }

    return null;
  }

  /**
   * Get points from curve entity
   */
  getCurvePoints(curve) {
    const points = [];

    if (curve.type === 'line') {
      points.push(
        new THREE.Vector3(curve.start.x, curve.start.y, curve.start.z || 0),
        new THREE.Vector3(curve.end.x, curve.end.y, curve.end.z || 0)
      );
    } else if (curve.type === 'circle' || curve.type === 'arc') {
      const segments = 32;
      const startAngle = curve.startAngle || 0;
      const endAngle = curve.endAngle || Math.PI * 2;

      for (let i = 0; i <= segments; i++) {
        const angle = startAngle + (endAngle - startAngle) * (i / segments);
        const x = curve.center.x + curve.radius * Math.cos(angle);
        const y = curve.center.y + curve.radius * Math.sin(angle);
        points.push(new THREE.Vector3(x, y, curve.center.z || 0));
      }
    } else if (curve.type === 'polyline' && curve.points) {
      for (const pt of curve.points) {
        points.push(new THREE.Vector3(pt.x, pt.y, pt.z || 0));
      }
    } else if (curve.points) {
      // Generic point array
      for (const pt of curve.points) {
        points.push(new THREE.Vector3(pt.x, pt.y, pt.z || 0));
      }
    }

    return points;
  }

  /**
   * Resample curve to specific number of points
   */
  resampleCurve(points, targetCount) {
    if (points.length === targetCount) return points;

    const resampled = [];
    for (let i = 0; i < targetCount; i++) {
      const t = i / (targetCount - 1);
      const index = t * (points.length - 1);
      const i1 = Math.floor(index);
      const i2 = Math.min(i1 + 1, points.length - 1);
      const fraction = index - i1;

      const p1 = points[i1];
      const p2 = points[i2];

      resampled.push(new THREE.Vector3(
        p1.x + (p2.x - p1.x) * fraction,
        p1.y + (p2.y - p1.y) * fraction,
        p1.z + (p2.z - p1.z) * fraction
      ));
    }

    return resampled;
  }

  /**
   * Smooth step function for G2 continuity
   */
  smoothStep(t) {
    return t * t * (3 - 2 * t);
  }

  /**
   * Remove surface
   */
  removeSurface(surfaceId) {
    const surfaceData = this.surfaces.get(surfaceId);
    if (!surfaceData) return;

    // Remove from scene
    this.scene.remove(surfaceData.mesh);

    // Dispose geometry and material
    surfaceData.geometry.dispose();
    surfaceData.mesh.material.dispose();

    // Remove from map
    this.surfaces.delete(surfaceId);

    this.viewer.engine.render();
  }

  /**
   * Get all surfaces
   */
  getAllSurfaces() {
    return Array.from(this.surfaces.values());
  }

  /**
   * Select surface
   */
  selectSurface(surfaceId) {
    // Deselect previous
    if (this.selectedSurfaceId) {
      const prevSurface = this.surfaces.get(this.selectedSurfaceId);
      if (prevSurface) {
        prevSurface.mesh.material.emissive.setHex(0x000000);
      }
    }

    // Select new
    this.selectedSurfaceId = surfaceId;
    const surface = this.surfaces.get(surfaceId);
    if (surface) {
      surface.mesh.material.emissive.setHex(0x333333);
    }

    this.viewer.engine.render();
  }

  /**
   * Clear all surfaces
   */
  clearAllSurfaces() {
    for (const surfaceId of this.surfaces.keys()) {
      this.removeSurface(surfaceId);
    }
  }
}

// Make available globally
if (typeof window !== 'undefined') {
  window.SurfaceModeler = SurfaceModeler;
}
